import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { EASE_OUT_EXPO } from '@/lib/animation';
import type { NavItem } from '@/data/navigation';

interface NavDropdownProps {
  item: NavItem;
  /** Matches the Navbar's transparent-over-hero state for trigger coloring. */
  transparent: boolean;
  /** Shared smooth-scroll / scroll-to-top handler from the Navbar. */
  onItemClick: (e: React.MouseEvent, path: string) => void;
}

/**
 * Desktop-only primary-nav dropdown. Opens on hover (with a small close delay
 * so the gap to the panel is forgiving) and on click; closes on item select,
 * outside click, and Escape. Trigger styling mirrors the sibling NavLinks.
 */
export function NavDropdown({ item, transparent, onItemClick }: NavDropdownProps) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLLIElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);

  const children = item.children ?? [];
  const isActive = children.some(
    (child) => child.path && !child.path.includes('#') && child.path === pathname
  );

  const openNow = () => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  return (
    <li
      ref={containerRef}
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'inline-flex items-center gap-1 text-sm transition-colors',
          transparent ? 'hover:text-brand-yellow' : 'hover:text-brand-yellow-text',
          isActive
            ? transparent
              ? 'text-brand-yellow'
              : 'text-brand-yellow-text'
            : transparent
              ? 'text-white/90'
              : 'text-brand-purple'
        )}
      >
        {item.label}
        <ChevronDown
          className={cn(
            'h-4 w-4 transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="menu"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18, ease: EASE_OUT_EXPO }}
            className="absolute right-0 top-full mt-3 min-w-60 rounded-2xl bg-white p-2 shadow-xl shadow-brand-purple/10 ring-1 ring-brand-purple/5"
          >
            {children.map((child) => (
              <li key={child.path} role="none">
                <NavLink
                  to={child.path ?? '#'}
                  role="menuitem"
                  onClick={(e) => {
                    onItemClick(e, child.path ?? '#');
                    setOpen(false);
                  }}
                  className="block rounded-xl px-4 py-2.5 text-sm text-brand-purple transition-colors hover:bg-brand-lightgray/70 hover:text-brand-yellow-text"
                >
                  {child.label}
                </NavLink>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </li>
  );
}
